import clsx from 'clsx';
import { Button, Stack, Text } from 'components';
import { useEffect } from 'react';
import { Drawer } from 'vaul';
import { AnimatePresence, motion } from 'framer-motion';

import style from './expanded.module.scss';
import { IdleScreen } from './idleScreen';
import { TranscribedScreen } from './transcribedScreen';
import { useAiScribe } from './useAiScribe';

export const formatDuration = (seconds: number) => {
  // Ensure we never have negative values
  const totalSeconds = Math.max(0, seconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export const PauseIcon = () => (
  <svg width="32" height="33" viewBox="0 0 32 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10.6667 25.8333C12.1333 25.8333 13.3333 24.6333 13.3333 23.1667V9.83332C13.3333 8.36666 12.1333 7.16666 10.6667 7.16666C9.2 7.16666 8 8.36666 8 9.83332V23.1667C8 24.6333 9.2 25.8333 10.6667 25.8333ZM18.6667 9.83332V23.1667C18.6667 24.6333 19.8667 25.8333 21.3333 25.8333C22.8 25.8333 24 24.6333 24 23.1667V9.83332C24 8.36666 22.8 7.16666 21.3333 7.16666C19.8667 7.16666 18.6667 8.36666 18.6667 9.83332Z"
      fill="#FF8C07"
    />
  </svg>
);

export const MicrophoneIcon = () => (
  <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.4984 19.8333C18.7117 19.8333 20.4984 18.0467 20.4984 15.8333V7.83334C20.4984 5.62001 18.7117 3.83334 16.4984 3.83334C14.285 3.83334 12.4984 5.62001 12.4984 7.83334V15.8333C12.4984 18.0467 14.285 19.8333 16.4984 19.8333ZM24.3784 15.8333C23.725 15.8333 23.1784 16.3133 23.0717 16.9667C22.525 20.1 19.7917 22.5 16.4984 22.5C13.205 22.5 10.4717 20.1 9.92502 16.9667C9.81835 16.3133 9.27169 15.8333 8.61835 15.8333C7.80502 15.8333 7.16502 16.5533 7.28502 17.3533C7.93835 21.3533 11.1384 24.4867 15.165 25.06V27.8333C15.165 28.5667 15.765 29.1667 16.4984 29.1667C17.2317 29.1667 17.8317 28.5667 17.8317 27.8333V25.06C21.8584 24.4867 25.0584 21.3533 25.7117 17.3533C25.845 16.5533 25.1917 15.8333 24.3784 15.8333Z"
      fill="#478262"
    />
  </svg>
);

const useAlertBeforeUnload = (condition: boolean) => {
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (condition) {
        e.preventDefault();
        e.returnValue = 'You have an active recording session. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [condition]);
};

const ExpandedDrawer = () => {
  const {
    initializing,
    state,
    status,
    isPaused,
    duration,
    clientName,
    prependedClientId,
    canvasRef,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    handleDrawerClose,
    error
  } = useAiScribe();

  useAlertBeforeUnload(status === 'recording');

  return (
    <Drawer.Root open={state === 'expanded'} modal={false} autoFocus={false} dismissible={false}>
      <Drawer.Portal>
        <div className={style.overlay} />
        <Drawer.Content className={style.content}>
          <Stack
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            width="100%"
            pt="16px"
            pb="12px"
            position="relative"
            bgcolor={status === 'transcribed' ? '#EFF8FA' : 'transparent'}
          >
            <Stack position="absolute" left="12px" top="12px">
              <Button
                icon={
                  <span className="material-icons" style={{ fontSize: '24px' }}>
                    {status === 'recording' && 'remove'}
                    {status === 'transcribed' && 'close'}
                    {status === 'idle' && 'close'}
                  </span>
                }
                shape="circle"
                theme="transparent"
                type="button"
                handleOnClick={handleDrawerClose}
              />
            </Stack>
            <Stack
              height="100%"
              display="flex"
              flexDirection="column"
              gap="6px"
              alignItems="center"
            >
              <Drawer.Title asChild>
                <Text size="large" weight="semibold" color="medium">
                  {clientName}
                </Text>
              </Drawer.Title>
              <Text size="small" weight="medium">
                IMPaCT ID: {prependedClientId}
              </Text>
            </Stack>
          </Stack>
          <AnimatePresence mode="sync">
            {status === 'idle' && (
              <motion.div
                key={status}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <IdleScreen startRecording={startRecording} initializing={initializing} />
              </motion.div>
            )}
            {status === 'recording' && (
              <motion.div
                key={status}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                style={{ paddingBottom: '128px', height: '100%', display: 'flex' }}
              >
                <Stack
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyItems="center"
                  justifyContent="center"
                  width="100%"
                  pt="36px"
                  height="100%"
                >
                  <Stack
                    width="100%"
                    display="flex"
                    gap="2px"
                    flexDirection="row"
                    alignItems="center"
                  >
                    <div className={clsx(style.canvasWrapper, isPaused && style.paused)}>
                      <canvas
                        ref={canvasRef}
                        style={{
                          width: '100%',
                          height: '187px'
                        }}
                      />
                    </div>
                    <div className={style.line} />
                  </Stack>
                  <Stack
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    alignItems="center"
                    flexGrow={1}
                  >
                    <div className={style.duration}>{formatDuration(duration)}</div>
                    <Stack width="176px">
                      {isPaused ? (
                        <Button
                          handleOnClick={resumeRecording}
                          text="Resume"
                          icon={
                            <Stack mr="4px">
                              <MicrophoneIcon />
                            </Stack>
                          }
                          theme="green"
                          size="large"
                        />
                      ) : (
                        <Button
                          handleOnClick={pauseRecording}
                          text="Pause"
                          icon={
                            <Stack mr="4px">
                              <PauseIcon />
                            </Stack>
                          }
                          theme="warning"
                          size="large"
                        />
                      )}
                    </Stack>
                    <Stack width="121px">
                      <Button
                        text="Submit"
                        theme="transparent"
                        size="small"
                        handleOnClick={() => stopRecording()}
                      />
                    </Stack>
                  </Stack>
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        className={style.error}
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 100 }}
                      >
                        <span className="material-icons">error_outline</span>
                        <p>{error}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Stack>
              </motion.div>
            )}
            {status === 'transcribed' && <TranscribedScreen />}
          </AnimatePresence>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default ExpandedDrawer;
